import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  Language, 
  Translations, 
  getTranslations, 
  detectBrowserLanguage,
  languageNames 
} from './translations';

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
  languageNames: Record<Language, string>;
  availableLanguages: Language[];
}

const I18nContext = createContext<I18nContextType | null>(null);

const STORAGE_KEY = 'nt3d_language';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored in languageNames) {
        return stored as Language;
      }
    }
    // Fall back to browser detection
    return detectBrowserLanguage();
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, lang);
    }
  };

  useEffect(() => {
    // Update document lang attribute for accessibility
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const value: I18nContextType = {
    language,
    setLanguage,
    t: getTranslations(language),
    languageNames,
    availableLanguages: Object.keys(languageNames) as Language[],
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextType {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

// Simple hook for just translations
export function useTranslations(): Translations {
  const { t } = useI18n();
  return t;
}
